import clsx from "clsx";
import React, { ReactNode, ForwardRefRenderFunction, useState } from "react";

interface Props
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  placeholder?: string;
  wrapperClassName?: string;
  onChange?: (text: string) => void;
  postfix?: { content?: ReactNode; className?: string };
  className?: string;
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  value?: string;
  disabled?: boolean;
  maxLength?: number;
  onClick?: (e: any) => void;
  error?: string;
  type?: string;
  variant?: "building";
}

const CommonInput: ForwardRefRenderFunction<HTMLInputElement, Props> = (
  {
    onChange,
    postfix,
    wrapperClassName,
    className,
    error,
    type,
    variant,
    maxLength = 200,
    ...props
  },
  ref
) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (onChange) {
      onChange(e.target.value);
    }
  };

  const onInputMaxLengthCheck = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.currentTarget.value.length >= maxLength) {
      e.currentTarget.value = e.currentTarget.value.slice(0, maxLength);
    }
    return e.currentTarget.value;
  };

  const [onFocusState, setOnFocusState] = useState(false);

  return (
    <div
      className={clsx([
        { "w-full": wrapperClassName === undefined },
        wrapperClassName,
      ])}
    >
      <div className="relative w-full" onClick={props.onClick}>
        <input
          type={type ?? "text"}
          className={clsx([
            "border focus:outline-none p-4 text-base w-full rounded-[3px] h-bottom-button design_min:placeholder:text-sm",
            className,
            {
              "border-brand-primary-magenta": error || onFocusState,
              "border-none bg-brand-neutral-05 text-brand-neutral-60":
                variant === "building",
            },
          ])}
          onFocus={() => setOnFocusState(true)}
          onBlur={() => setOnFocusState(false)}
          maxLength={maxLength}
          onInput={onInputMaxLengthCheck}
          {...props}
          onChange={handleChange}
          ref={ref}
        />
        {postfix && (
          <div
            className={clsx([
              "absolute right-1 top-1/2 -translate-y-1/2 w-fit h-fit",
              postfix?.className ?? "",
            ])}
          >
            {typeof postfix === "object" ? postfix?.content ?? "" : postfix}
          </div>
        )}
      </div>
      {error && (
        <div className="text-brand-primary-magenta text-xs px-4 pt-1">
          *{error}
        </div>
      )}
    </div>
  );
};

export default React.forwardRef(CommonInput);
